import { JSX, useEffect, useState } from 'react';
import { IconPrinter, IconDownload } from '@lmig/lmds-react-icons';
import { Button } from '@lmig/lmds-react-button';
import { Heading, BodyText } from '@lmig/lmds-react-typography';
import { Form } from '@lmig/lmds-react-form';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@lmig/lmds-react-table';
import { Alert } from '@lmig/lmds-react-alert';
import TextField from '@mui/material/TextField';
import './AgencySweepReport.scss';
import getPayments from 'Api/api/agencySweepApi';
import ExportAgencySweepUtility from 'Utilities/ExportAgencySweepUtility';
import { useUserData } from 'Foundation/User/UserContext';

const AgencySweepReport = () => {
  const { userData } = useUserData();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [agencySweepData, setAgencySweepData] = useState<Array<IAgencySweepResults> | null>(null);
  const [startDateFormatted, setStartDateFormatted] = useState<string>('');
  const [endDateFormatted, setEndDateFormatted] = useState<string>('');
  const [agentNumberInputHasError, setAgentNumberInputHasError] = useState(false);
  const agentHelperText = 'Enter 6 digit agent number; leave blank for all';
  const [agentNumber, setAgentNumber] = useState('');
  const [interestNumber, setInterestNumber] = useState('');
  const exportUtility = ExportAgencySweepUtility.getInstance();
  const [hasDateError, setHasDateError] = useState(false);
  const [dateErrorMessage, setDateErrorMessage] = useState<string>('');
  const [invalidSubCodeError, setInvalidSubCodeError] = useState(false);
  const [invalidSubCodeErrorMessage, setInvalidSubCodeErrorMessage] = useState<string>('');

  const formatAndSetStartDate = (startDate: Date) => {
    let formattedDate = formatDate(startDate);
    setStartDateFormatted(formattedDate);
    resetDateErrorsOnValidInputs(formattedDate, endDateFormatted);
  };

  const formatAndSetEndDate = (endDate: Date) => {
    let formattedDate = formatDate(endDate);
    setEndDateFormatted(formattedDate);
    resetDateErrorsOnValidInputs(startDateFormatted, formattedDate);
  };

  const resetDateErrorsOnValidInputs = (startDate: string, endDate: string) => {
    let hasErrors = true;
    //implies error is in reference to unset date
    if (hasDateError && startDate.length !== 0 && endDate.length !== 0) {
      hasErrors = false;
    }
    if (!hasErrors) {
      setHasDateError(false);
      setDateErrorMessage('');
    }
  };

  const formatDate = (date: Date) => {
    let formattedDate =
      ('0' + (date.getUTCMonth() + 1)).slice(-2) +
      '/' +
      ('0' + date.getUTCDate()).slice(-2) +
      '/' +
      date.getUTCFullYear();
    return formattedDate;
  };

  const searchAgencySweep = async () => {
    if (isFormValid()) {
      setIsLoaded(false);
      setIsLoading(true);
      setInvalidSubCodeError(false);
      let results = await getPayments(agentNumber, startDateFormatted, endDateFormatted, interestNumber);
      if (results && !results?.error && results?.length > 0) {
        formatAgencySweepResults(results);
        setAgencySweepData(results);
      } else {
        setAgencySweepData([]);
        if (results?.error) {
          if (results?.error?.code === '404') {
            console.log('results?.error?.message: ' + results?.error?.message);
            if (!invalidSubCodeError) {
              setInvalidSubCodeError(true);
            }
            if (invalidSubCodeErrorMessage !== results?.error?.message) {
              setInvalidSubCodeErrorMessage(results?.error?.message);
            }
          }
        }
      }
      setIsLoading(false);
      setIsLoaded(true);
    } else {
      setAgencySweepData([]);
      setIsLoading(false);
      setIsLoaded(true);
    }
  };

  const formatAgencySweepResults = (results: any) => {
    results.forEach((policy: { type: string; forceCode: string; amount: string }) => {
      if (policy.forceCode === 'VO') {
        policy.type = 'Void';
        policy.amount = '($' + policy.amount + ')';
      }
    });
  };

  const isFormValid = () => {
    let valid = true;
    let sDate = new Date(startDateFormatted);
    let eDate = new Date(endDateFormatted);
    if (agentNumber.length > 0 && agentNumber.length < 6) {
      setAgentNumberInputHasError(true);
      valid = false;
    }
    if (startDateFormatted.length === 0 || endDateFormatted.length === 0) {
      valid = false;
      setHasDateError(true);
      setDateErrorMessage('Both a Start and End date are required.');
    }
    if (sDate.getTime() > eDate.getTime()) {
      valid = false;
      setHasDateError(true);
      setDateErrorMessage('Start date must be earlier than end date.');
    }
    if (sDate.getTime() < eDate.getTime() && agentNumber.length === 0 && interestNumber.length === 0) {
      valid = false;
      setHasDateError(true);
      setDateErrorMessage(
        'For performance reasons, this search is not allowed. Please enter a policy or agent number or search for payments from a single day.',
      );
    }

    if (valid && hasDateError) {
      setHasDateError(false);
      setDateErrorMessage('');
    }

    return valid;
  };

  const printAgencySweep = () => {
    window.print();
  };

  const exportAgencySweep = () => {
    if (agencySweepData != null) exportUtility.ExportData(agencySweepData);
  };

  const validateAgentNumberInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let inputText = e?.target.value;
    let isnum = /^\d+$/.test(inputText);
    if (isnum || inputText.length === 0) {
      setAgentNumber(inputText);
    }
    if (agentNumberInputHasError && (inputText.length === 6 || inputText.length === 0)) {
      setAgentNumberInputHasError(false);
    }
  };

  useEffect(() => {
    var agentNumber = document.getElementById('agent-number');
    if (typeof agentNumber != 'undefined' && agentNumber != null) {
      agentNumber.setAttribute('maxlength', '6');
      agentNumber.setAttribute('aria-label', 'agent-number');
    }
    var sDate = document.getElementById('from-date');
    if (typeof sDate != 'undefined' && sDate != null) {
      sDate.setAttribute('aria-label', 'from-date');
    }
    var eDate = document.getElementById('to-date');
    if (typeof eDate != 'undefined' && eDate != null) {
      eDate.setAttribute('aria-label', 'to-date');
    }
  }, []);

  return (
    <div className="agency-sweep-report">
      {hasDateError && (
        <Alert className="alert-message" highlightType="negative">
          {dateErrorMessage}
        </Alert>
      )}
      <div className="search-header">
        <Heading align="left" type="h4-light">
          Agency Sweep
        </Heading>
        <div style={{ display: 'none' }}>{`The current userType is ${userData?.userType}`}</div>
        <div className="search-terms">
          <Form onSubmit={() => searchAgencySweep()}>
            <TextField
              id="from-date"
              label="From"
              type="date"
              aria-label="start-date-input"
              className="date-field"
              variant="outlined"
              onChange={(event: any) => formatAndSetStartDate(new Date(event?.target.value))}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              id="to-date"
              label="To"
              type="date"
              aria-label="end-date-input"
              className="date-field"
              variant="outlined"
              onChange={(event: any) => formatAndSetEndDate(new Date(event?.target.value))}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              id="agent-number"
              label="Agent number"
              variant="outlined"
              className="agent-number offset-text-field"
              helperText={agentHelperText}
              value={agentNumber}
              onChange={(event: any) => validateAgentNumberInput(event)}
              error={agentNumberInputHasError}
              color={!agentNumberInputHasError ? 'primary' : 'secondary'}
            />
            <TextField
              id="policy-account-number"
              label="Policy or account number"
              variant="outlined"
              helperText="Leave blank for all"
              className="offset-text-field"
              value={interestNumber}
              onChange={(event: any) => setInterestNumber(event?.target.value)}
            />
            <Button id="run-agency-sweep-search" data-testid="run-agency-sweep-search" variant="primary" onClick={() => searchAgencySweep()}>
              Run
            </Button>
          </Form>
        </div>
      </div>
      <div className="print-export-functions">
        <button
          disabled={!agencySweepData}
          className={!agencySweepData ? 'disabledBtnIcon' : ''}
          onClick={() => exportAgencySweep()}
        >
          <IconDownload disabled={!agencySweepData} className={!agencySweepData ? 'disabledBtnIcon' : ''} />
          Export
        </button>
        <button onClick={() => printAgencySweep()}>
          <IconPrinter />
          Print
        </button>
      </div>
      {isLoaded ? (
        agencySweepData && agencySweepData?.length > 0 ? (
          <Table className="agency-sweep-results" rowStyle="zebra">
            <TableHeader>
              <TableRow>
                <TableCell type="colHead">Transaction date</TableCell>
                <TableCell type="colHead">Account/policy number</TableCell>
                <TableCell type="colHead">Customer name</TableCell>
                <TableCell type="colHead">Payment amount</TableCell>
                <TableCell type="colHead">Payment type</TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>{agencySweepData?.map((policy, index) => AgencyPaymentRow(policy, index))}</TableBody>
          </Table>
        ) : !invalidSubCodeError ? (
          <BodyText tag="h4" className="no-data-message">
            No data found, please try again
          </BodyText>
        ) : (
          <BodyText tag="h4" className="no-data-message">
            {invalidSubCodeErrorMessage}
          </BodyText>
        )
      ) : isLoading ? (
        <BodyText tag="h4" className="no-data-message">
          Loading...
          <img src="/-/media/Project/Safeco/shared/Rewards/spinner.gif" alt="loading" className="loading-spinner" />
        </BodyText>
      ) : (
        <></>
      )}
    </div>
  );
};

function AgencyPaymentRow(policy: IAgencySweepResults, index: number): JSX.Element {
  try {
    if (policy) {
      return (
        <TableRow key={index}>
          <TableCell>{policy.requestDate ? new Date(policy.requestDate).toLocaleDateString('en-US') : ''}</TableCell>
          <TableCell>{policy.interestNumber}</TableCell>
          <TableCell>{policy.customerName}</TableCell>
          <TableCell>{policy.amount}</TableCell>
          <TableCell>{policy.type}</TableCell>
        </TableRow>
      );
    }
  } catch (error) {
    console.error('Error rendering AgencyPaymentRow: ', error);
  }
  return <></>;

}

export default AgencySweepReport;
